import { motion } from 'framer-motion';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiArrowLeft, FiMail } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import Button from '../../components/ui/Button';
import InputField from '../../components/ui/InputField';
import LanguageSwitcher from '../../components/ui/LanguageSwitcher';
import { useAppDispatch } from '../../store/hooks';
import { forgotPassword } from '../../store/slices/authSlice';

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  // const { forgotPassword } = useAuth();
  const dispatch = useAppDispatch()

  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.4 }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
      setError(t('auth.invalidEmail'));
      return;
    }

    setIsLoading(true);

    try {
      // await forgotPassword(email);
      await dispatch(forgotPassword(email))
      setSuccess(true);
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Header with Language Switcher */}
      <header className="py-4 px-6 flex justify-between items-center">
        <Link to="/" className="flex items-center space-x-2">
          <motion.div
            initial={{ rotate: -10 }}
            animate={{ rotate: 0 }}
            transition={{ duration: 0.5 }}
            className="text-2xl font-bold text-primary"
          >
            Dine.ai
          </motion.div>
        </Link>

        <LanguageSwitcher />
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8"
        >
          <motion.div variants={itemVariants} className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {t('auth.resetPassword')}
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              {t('auth.resetPasswordSubtitle')}
            </p>
          </motion.div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-6"
            >
              {error}
            </motion.div>
          )}

          {success ? (
            <motion.div
              variants={itemVariants}
              className="text-center space-y-6"
            >
              <div className="bg-green-100 dark:bg-green-900/30 border border-green-400 dark:border-green-700 text-green-700 dark:text-green-400 px-4 py-5 rounded-md">
                <p className="mb-2 font-semibold">{t('auth.resetPasswordSuccess')}</p>
                <p className="text-sm">
                  We've sent instructions to <span className="font-medium">{email}</span>
                </p>
              </div>

              <Link to="/">
                <Button
                  variant="outline"
                  fullWidth
                  leftIcon={<FiArrowLeft />}
                >
                  {t('auth.backToLogin')}
                </Button>
              </Link>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit}>
              <motion.div variants={itemVariants} className="space-y-6">
                <InputField
                  type="email"
                  label={t('auth.email')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.emailPlaceholder')}
                  icon={<FiMail />}
                  required
                />

                <Button
                  type="submit"
                  fullWidth
                  size="lg"
                  isLoading={isLoading}
                >
                  {t('auth.sendResetLink')}
                </Button>

                <div className="text-center">
                  <Link
                    to="/"
                    className="text-primary hover:text-primary-dark font-medium inline-flex items-center"
                  >
                    <FiArrowLeft className="mr-2" />
                    {t('auth.backToLogin')}
                  </Link>
                </div>
              </motion.div>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default ForgotPassword;